import { isSupabaseConfigured, supabase } from '../../lib/supabase'
import { activeTenant } from '../../lib/tenant'
import type {
  PublicAvailabilitySnapshot,
  PublicProject,
  PublicProjectUnit,
} from './types'

const PUBLIC_SITE_KEY = 'webapp_public_site'

type PublicUnitRow = {
  id: string
  project_id: string
  unit_code: string
  unit_name: string | null
  property_type: string
  building: string | null
  floor: string | null
  bedrooms: number | null
  bathrooms: number | string | null
  parking_spaces: number | null
  area_m2: number | string | null
  price: number | string | null
  currency: string
  status: string
  updated_at: string
}

type PublicProjectRow = {
  id: string
  project_name: string
  description: string | null
  location: string | null
}

function numberOrNull(value: number | string | null | undefined): number | null {
  if (value === null) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function mapProject(row: PublicProjectRow): PublicProject {
  return {
    id: row.id,
    name: row.project_name,
    description: row.description,
    location: row.location,
  }
}

function mapUnit(
  row: PublicUnitRow,
  project: PublicProjectRow | undefined,
): PublicProjectUnit {
  return {
    id: row.id,
    projectId: row.project_id,
    projectName: project?.project_name ?? `Proyecto ${activeTenant.code}`,
    projectLocation: project?.location ?? null,
    unitCode: row.unit_code,
    unitName: row.unit_name,
    propertyType: row.property_type,
    building: row.building,
    floor: row.floor,
    bedrooms: row.bedrooms,
    bathrooms: numberOrNull(row.bathrooms),
    parkingSpaces: row.parking_spaces,
    areaM2: numberOrNull(row.area_m2),
    price: numberOrNull(row.price),
    currency: row.currency,
    status: row.status,
    updatedAt: row.updated_at,
  }
}

async function loadPublishedProjectIds(): Promise<string[]> {
  if (!supabase) return []

  const { data, error } = await supabase
    .from('company_settings')
    .select('value')
    .eq('company_id', activeTenant.companyId)
    .eq('key', PUBLIC_SITE_KEY)
    .maybeSingle()

  if (error || !data?.value || typeof data.value !== 'object') return []

  const value = data.value as {
    projects?: { publishedProjectIds?: unknown }
  }
  const ids = value.projects?.publishedProjectIds
  return Array.isArray(ids)
    ? ids.filter((id): id is string => typeof id === 'string')
    : []
}

export async function loadPublicAvailability(): Promise<PublicAvailabilitySnapshot> {
  if (!isSupabaseConfigured || !supabase) {
    return {
      state: 'unconfigured',
      message: 'La disponibilidad en línea todavía no está configurada.',
      units: [],
      projects: [],
    }
  }

  const configuredProjectIds = await loadPublishedProjectIds()

  let unitQuery = supabase
    .from('project_units')
    .select(
      'id, project_id, unit_code, unit_name, property_type, building, floor, bedrooms, bathrooms, parking_spaces, area_m2, price, currency, status, updated_at',
    )
    .eq('company_id', activeTenant.companyId)
    .eq('is_published', true)
    .in('status', ['available', 'held', 'reserved'])
    .order('unit_code')

  if (configuredProjectIds.length) {
    unitQuery = unitQuery.in('project_id', configuredProjectIds)
  }

  const { data: unitData, error: unitError } = await unitQuery

  if (unitError) {
    return {
      state: 'error',
      message: 'No fue posible consultar la disponibilidad en este momento.',
      units: [],
      projects: [],
    }
  }

  const unitRows = (unitData ?? []) as PublicUnitRow[]

  const fallbackProjectIds = [...new Set(unitRows.map(unit => unit.project_id))]
  const projectIds = configuredProjectIds.length
    ? configuredProjectIds
    : fallbackProjectIds

  if (!projectIds.length) {
    return {
      state: 'ready',
      units: [],
      projects: [],
      loadedAt: new Date().toISOString(),
    }
  }

  const { data: projectData, error: projectError } = await supabase
    .from('projects')
    .select('id, project_name, description, location')
    .eq('company_id', activeTenant.companyId)
    .in('id', projectIds)
    .order('project_name')

  if (projectError) {
    return {
      state: 'error',
      message: 'No fue posible consultar los proyectos publicados.',
      units: [],
      projects: [],
    }
  }

  const projectRows = (projectData ?? []) as PublicProjectRow[]
  const projects = new Map(projectRows.map(project => [project.id, project]))

  return {
    state: 'ready',
    projects: projectRows.map(mapProject),
    units: unitRows.map(unit => mapUnit(unit, projects.get(unit.project_id))),
    loadedAt: new Date().toISOString(),
  }
}
